import { AXIOS_INSTANCE } from '@api'
import { brandNameAction } from '@api/product'
import { GeneralResMock } from '@type/general.type'
import { PatientSAttrProps } from '@type/patient.types'
import { StaffMeta, StaffSAttrProps } from '@type/staff.types'
import { getError } from '@util'
import { ArchiveFoldersResponse, getArchivePatientName } from '../types/archive.types'
import { FacilitySchema } from '../types/facility.type'
import { InventoryLocationDataSchema, InventoryLocationResponse } from '../types/inventoryLocation.type'
import { UnitDataMock, UnitIncludedMock } from '../types/unit'

export type SelectOption = { value: string; label: string }

export type PaginatedSelectOptions = {
  options: SelectOption[]
  hasMore: boolean
  page: number
}

export type ArchiveFolderOption = SelectOption & {
  patient_id?: string
  folder_number?: string
}

export type InventoryLocationOption = { value: string; label: string; facilityId: string; inventoryLocationId: string }

export const loadDoctorOptions = async (inputValue: string): Promise<SelectOption[]> => {
  try {
    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<StaffSAttrProps[]>>('/staffs', {
      params: {
        role: 'doctor',
        ...(inputValue?.trim() && { query: inputValue.trim() }),
        'page[size]': 50
      },
      skipNavigationFeedback: true
    })
    const list = data?.data ?? []
    return list.map((s: StaffSAttrProps) => ({
      value: s.id,
      label: [s.attributes?.first_name, s.attributes?.last_name].filter(Boolean).join(' ').trim() || '—'
    }))
  } catch (error: any) {
    getError(error)
    return []
  }
}

export const loadPatientOptions = async (inputValue: string): Promise<SelectOption[]> => {
  try {
    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<PatientSAttrProps[]>>('/patients', {
      params: {
        ...(inputValue?.trim() && { query: inputValue.trim() }),
        'page[size]': 30,
        stat: true
      },
      skipNavigationFeedback: true
    })
    const list = data?.data ?? []
    return list.map((p: PatientSAttrProps) => ({
      value: p.id,
      label: [p.attributes?.first_name, p.attributes?.last_name].filter(Boolean).join(' ').trim() || '—'
    }))
  } catch (error: any) {
    getError(error)
    return []
  }
}

export const loadUnitOptions = async (inputValue: string): Promise<SelectOption[]> => {
  try {
    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<UnitDataMock[], UnitIncludedMock[]>>('/units', {
      params: {
        ...(inputValue?.trim() && { query: inputValue.trim() }),
        'page[size]': 50
      },
      skipNavigationFeedback: true
    })
    const included = data?.included ?? []
    const list = data?.data ?? []
    return list.map((u: UnitDataMock) => ({
      value: u.id,
      label:
        u.attributes.name +
        ` (${included.find(i => i.id === u.relationships.facility.data?.id && i.type === 'facility')?.attributes.name})`
    }))
  } catch (error: any) {
    getError(error)
    return []
  }
}

export const loadStaffOptionsPage = async (
  inputValue: string,
  { exclude_roles, page = 1, pageSize = 30 }: { exclude_roles?: string; page?: number; pageSize?: number } = {}
): Promise<PaginatedSelectOptions> => {
  try {
    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<StaffSAttrProps[], unknown, StaffMeta>>('/staffs', {
      params: {
        ...(inputValue?.trim() && { query: inputValue.trim() }),
        'page[number]': page,
        'page[size]': pageSize,
        ...(exclude_roles && { exclude_roles })
      },
      skipNavigationFeedback: true
    })
    const list = data?.data ?? []
    const options = list.map((s: StaffSAttrProps) => ({
      value: s.id,
      label: [s.attributes?.first_name, s.attributes?.last_name].filter(Boolean).join(' ').trim() || '—'
    }))
    const pagination = data?.meta?.pagination
    const hasMore = pagination ? pagination.page < pagination.pages : list.length >= pageSize
    return { hasMore, options, page: pagination?.page ?? page }
  } catch (error: any) {
    getError(error)
    return { hasMore: false, options: [], page }
  }
}

export const loadStaffOptions = async (
  inputValue: string,
  { exclude_roles }: { exclude_roles?: string } = {}
): Promise<SelectOption[]> => {
  const { options } = await loadStaffOptionsPage(inputValue, { exclude_roles, page: 1, pageSize: 50 })
  return options
}

export const loadBrandNameProductOptions = async (inputValue: string): Promise<SelectOption[]> => {
  try {
    return await brandNameAction(inputValue)
  } catch (error: any) {
    getError(error)
    return []
  }
}

export const loadArchiveFolderOptions = async (inputValue: string): Promise<ArchiveFolderOption[]> => {
  try {
    const { data } = await AXIOS_INSTANCE.get<ArchiveFoldersResponse>('/archive_folders', {
      params: {
        ...(inputValue?.trim() && { query: inputValue.trim() }),
        'page[size]': 50
      },
      skipNavigationFeedback: true
    })
    const list = data?.data ?? []
    const included = data?.included ?? []
    return list.map(folder => {
      const patientName = getArchivePatientName(folder, included)
      const folderNumber = folder.attributes.folder_number
      return {
        value: folder.id,
        label: patientName !== '—' ? patientName : folderNumber || `Folder #${folder.id}`,
        patient_id: folder.relationships.patient?.data?.id,
        folder_number: folderNumber
      }
    })
  } catch (error: any) {
    getError(error)
    return []
  }
}

export const loadFacilityOptions = async (inputValue: string): Promise<SelectOption[]> => {
  try {
    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<FacilitySchema[]>>('/facilities', {
      params: {
        ...(inputValue?.trim() && { query: inputValue.trim() }),
        'page[size]': 50
      },
      skipNavigationFeedback: true
    })
    const list = data?.data ?? []
    return list.map((f: FacilitySchema) => ({
      value: f.id,
      label: f.attributes.name
    }))
  } catch (error: any) {
    getError(error)
    return []
  }
}

export const loadInventoryLocationOptions = async (
  inputValue: string,
  is_main_pharmacy?: boolean
): Promise<InventoryLocationOption[]> => {
  try {
    const { data } = await AXIOS_INSTANCE.get<InventoryLocationResponse>('/inventory_locations', {
      params: {
        ...(inputValue?.trim() && { query: inputValue.trim() }),
        'page[size]': 50,
        ...(is_main_pharmacy !== undefined && { is_main_pharmacy })
      },
      skipNavigationFeedback: true
    })
    const list = data?.data ?? []
    const included = data?.included ?? []
    return list.map((l: InventoryLocationDataSchema) => ({
      value: l.attributes.kind,
      facilityId: l.relationships.facility.data?.id ?? '',
      inventoryLocationId: l.id,
      label: `${l.attributes.name}`
    }))
  } catch (error: any) {
    getError(error)
    return []
  }
}
